import React from 'react'
import {
    FilterOp,
    useCountChatsQuery,
    UserNotificationSound,
} from '~graphql/generated/graphql'
import { useClinic } from './clinic-context'
import { useUserContext } from '~config/user-context'
import { configureAbly, useChannel } from '@ably-labs/react-hooks'
import {
    ChannelTypeNames,
    constructChannel,
    getAblyMessageData,
} from '~utils/ably-helpers'
import { AUDIO_SOUNDS, playAudio } from '~utils/audio-helpers'
import {
    DESKTOP_NOTIFICATION_TAGS,
    showDesktopNotification,
} from '~utils/desktop-notifications'
import { gtFloatFilter, inStringArrFilter } from '~utils/query-helpers'

type UnreadMessagesContextType = {
    unreadMessages: number
    refreshUnreadMessages: () => void
}

const UnreadMessagesContext = React.createContext<UnreadMessagesContextType>({
    unreadMessages: 0,
    refreshUnreadMessages: () => {},
})

export function useUnreadMessages() {
    return React.useContext(UnreadMessagesContext)
}

export default function UnreadMessagesProvider({
    children,
}: React.PropsWithChildren) {
    return (
        <UnreadMessagesContext.Provider value={useUnreadMessagesValues()}>
            {children}
        </UnreadMessagesContext.Provider>
    )
}

function useUnreadMessagesValues(): UnreadMessagesContextType {
    const { user } = useUserContext()
    const { clinic, clinicGroup } = useClinic()

    const clinicsArray = clinic
        ? [clinic.id]
        : clinicGroup?.clinic_group_clinics.map(c => c.clinic.id) || []

    const { data, refetch: refreshUnreadMessages } = useCountChatsQuery({
        variables: {
            where: [
                inStringArrFilter('clinic', clinicsArray),
                gtFloatFilter('clinic_unread_count', 0),
            ],
        },
        skip: (!clinic && !clinicGroup) || !user,
    })

    const unreadMessages = data?.countChats || 0

    configureAbly({
        authUrl: `${process.env.GATSBY_REST_API_URL}/ably/auth`,
    })

    function handleChatNotification(message: unknown) {
        if (
            user?.enabled_notification_sounds?.includes(
                UserNotificationSound.Inbox,
            )
        ) {
            playAudio(AUDIO_SOUNDS.inbox)
            showDesktopNotification('New inbox message', {
                body:
                    getInboxPreviewFromAblyMessage(message) ??
                    'You have a new message.',
                tag: DESKTOP_NOTIFICATION_TAGS.inbox,
            })
        }

        refreshUnreadMessages()
    }

    useChannel(
        constructChannel(ChannelTypeNames.chatNotification, clinic?.id || ''),
        handleChatNotification,
    )

    useChannel(
        constructChannel(
            ChannelTypeNames.chatNotification,
            clinicGroup?.id || '',
        ),
        handleChatNotification,
    )

    return {
        unreadMessages,
        refreshUnreadMessages,
    }
}

const INBOX_PREVIEW_MAX_LENGTH = 120

/**
 * The server publishes the raw `CreateMessageInput` on `chatNotification-*`,
 * so the message text (if any) is at `data.text`. Attachment-only messages
 * have no text - the caller falls back to generic copy.
 */
function getInboxPreviewFromAblyMessage(message: unknown): string | null {
    const data = getAblyMessageData(message)
    if (typeof data !== 'object' || data === null || !('text' in data)) {
        return null
    }
    const { text } = data
    if (typeof text !== 'string') return null
    const trimmed = text.trim()
    if (!trimmed) return null
    return trimmed.length > INBOX_PREVIEW_MAX_LENGTH
        ? `${trimmed.slice(0, INBOX_PREVIEW_MAX_LENGTH)}…`
        : trimmed
}
